import React from 'react';
import propTypes from 'prop-types';
import {
  Button,
  Columns,
  Column,
  ModalCardBody,
  ModalCardHeader,
  ModalCardTitle,
  ModalCard,
  ModalBackground,
  Modal,
  Control,
  Field as BulmaField,
} from 'src/ui/mui-ui-kit';
import { usePoseWithBulma } from 'src/ui/hooks';
import {
  FadeInDiv,
  AnimatedCard,
  UiInputAdapter,
  InnerLoader,
  UiToggleAdapter,
  UiTooltip,
  submitAndReset,
  UiSelectAdapter,
} from 'src/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, Field } from 'react-final-form';
import { composeValidators, email, required } from 'src/ui/validations';
import { PHONE_MASK } from 'src/ui/const';
import {
  PERSON_TYPE_OWNER,
  PERSON_TYPE_EMPLOYEE,
  PERSON_TYPE_INSURANCE,
} from 'src/user/const';
/* eslint-disable camelcase */

export function AddPersonModal({
  isActive,
  onCancel,
  onSubmit,
  type,
  insuranceGroupNames,
  isBankAdmin,
}) {
  const [currentPose, isModalVisible, onPoseComplete] = usePoseWithBulma(
    isActive
  );
  /* eslint-disable no-nested-ternary */
  const label =
    type === PERSON_TYPE_OWNER
      ? 'Owner'
      : type === PERSON_TYPE_EMPLOYEE
      ? 'Employee'
      : type === PERSON_TYPE_INSURANCE
      ? 'Insurance User'
      : 'Employee';

  return (
    <Form
      onSubmit={onSubmit}
      render={({ form, hasValidationErrors, submitting, handleSubmit }) => {
        const submit = submitAndReset(handleSubmit, form);

        return (
          <form onSubmit={submit}>
            <Modal isActive={isModalVisible} className="add-person-modal">
              <Field
                defaultValue={type}
                name="type"
                render={({ input }) => <input {...input} type="hidden" />}
              />
              <ModalBackground
                tag={FadeInDiv}
                pose={currentPose}
                onPoseComplete={onPoseComplete}
              />
              <ModalCard tag={AnimatedCard} pose={currentPose}>
                <InnerLoader visible={submitting} />
                <ModalCardHeader>
                  <ModalCardTitle
                    tag="h5"
                    className="has-text-weight-semibold columns is-vcentered"
                  >
                    <Column isSize="narrow" tag="span">
                      <span className="icon is-medium has-icon-gradient-bright">
                        <FontAwesomeIcon
                          icon={['fas', 'user-circle']}
                          size="lg"
                        />
                      </span>
                    </Column>
                    <Column tag="span" hasTextColor="primary" isPaddingless>
                      Add {label}
                    </Column>
                  </ModalCardTitle>
                </ModalCardHeader>
                <ModalCardBody>
                  <Columns isMultiline>
                    <Column isSize="1/2">
                      {type === PERSON_TYPE_INSURANCE && isBankAdmin && (
                        <Field
                          component={UiToggleAdapter}
                          id={`isAdmin${type}`}
                          name="isAdmin"
                          type="checkbox"
                        >
                          Account Admin
                          <UiTooltip content="Account admins can change business information and invite other people to join" />
                        </Field>
                      )}
                      {type !== PERSON_TYPE_INSURANCE && (
                        <Field
                          component={UiToggleAdapter}
                          id={`isAdmin${type}`}
                          name="isAdmin"
                          type="checkbox"
                        >
                          Account Admin
                          <UiTooltip content="Account admins can change business information and invite other people to join" />
                        </Field>
                      )}
                    </Column>

                    <Column isSize="1/2">
                      {type !== PERSON_TYPE_INSURANCE ? (
                        <Field
                          component={UiToggleAdapter}
                          id={`isSigner${type}`}
                          name="isSigner"
                          type="checkbox"
                        >
                          Account Signer
                        </Field>
                      ) : (
                        <Field
                          component={UiSelectAdapter}
                          name="insuranceGroupName"
                          id="insuranceGroupName"
                          autoFocus
                          options={
                            insuranceGroupNames
                              ? insuranceGroupNames.map(({ name, id }) => ({
                                  label: name,
                                  value: id,
                                }))
                              : []
                          }
                          label="Group Name"
                        />
                      )}
                    </Column>

                    <Column isSize="1/2">
                      <Field
                        component={UiInputAdapter}
                        id={`firstName${type}`}
                        name="firstName"
                        label="First Name"
                        required
                        validate={composeValidators('First Name', required)}
                      />
                    </Column>
                    <Column isSize="1/2">
                      <Field
                        component={UiInputAdapter}
                        id={`lastName${type}`}
                        name="lastName"
                        label="Last Name"
                        required
                        validate={composeValidators('Last Name', required)}
                      />
                    </Column>
                    <Column isSize="1/2">
                      <Field
                        component={UiInputAdapter}
                        id={`primaryNumber${type}`}
                        name="primaryNumber"
                        label="Phone"
                        required
                        mask={PHONE_MASK}
                        validate={composeValidators('Phone', required)}
                      />
                    </Column>
                    <Column isSize="1/2">
                      <Field
                        component={UiInputAdapter}
                        id={`emailAddress${type}`}
                        name="emailAddress"
                        label="E-mail"
                        required
                        validate={composeValidators('E-mail', required, email)}
                      />
                    </Column>
                    <Column isSize="full">
                      <Field
                        component={UiToggleAdapter}
                        id={`sendEmail${type}`}
                        name="sendEmail"
                        type="checkbox"
                        initialValue={type !== PERSON_TYPE_INSURANCE}
                      >
                        Send invitation email
                      </Field>
                    </Column>
                  </Columns>
                  <BulmaField isGrouped="centered">
                    <Control>
                      <Button
                        hasTextColor="info"
                        onClick={() => onCancel(form)}
                      >
                        Cancel
                      </Button>
                    </Control>
                    <Control>
                      <Button
                        isColor="gradient-bright"
                        type="submit"
                        disabled={submitting || hasValidationErrors}
                      >
                        Create User
                      </Button>
                    </Control>
                  </BulmaField>
                </ModalCardBody>
              </ModalCard>
            </Modal>
          </form>
        );
      }}
    />
  );
}

AddPersonModal.defaultProps = {
  insuranceGroupNames: [],
  isBankAdmin: false,
};
AddPersonModal.propTypes = {
  isActive: propTypes.bool.isRequired,
  isBankAdmin: propTypes.bool,
  onCancel: propTypes.func.isRequired,
  onSubmit: propTypes.func.isRequired,
  type: propTypes.oneOf([
    PERSON_TYPE_EMPLOYEE,
    PERSON_TYPE_OWNER,
    PERSON_TYPE_INSURANCE,
  ]).isRequired,
  financialInstitutionName: propTypes.string.isRequired,
  businessName: propTypes.string.isRequired,
  insuranceGroupNames: propTypes.oneOfType([
    propTypes.array, // It can be an array
    propTypes.oneOf([null]), // Or it can be null
  ]),
};
